// Central Administration + Platform Console navigation model — the single
// source of truth for the Administration hub, the Platform Console hub, their
// category landing pages, the command palette and role/capability visibility.
//
// Access is CAPABILITY-driven: each item/category declares `cap` (a permission
// string from the RBAC catalog) and visibility is evaluated with the user's
// effective permissions via `hasPermission`. Roles carry the capabilities
// (see backend/rbac.py) — nothing is hardcoded per user here.
//
// `audience` separates customer Workspace Administration from the Cloverleaf-only
// Platform Console. `future: true` renders a disabled "Coming soon" tile.

import {
  Shield, Server, Building2, Sparkles, DollarSign, Settings,
  Users, KeyRound, ScrollText, Activity, Rocket, ListChecks, FileClock, GitBranch,
  Gauge, HardDrive, UserPlus, Mailbox, Ticket, Bot, Award, ShieldCheck,
  BadgeCheck, Receipt, CreditCard, Mail, MessageSquare, CalendarClock,
  Cpu, Mic, LifeBuoy, Flag, Boxes, GraduationCap, Download, Globe, Pyramid,
} from 'lucide-react';

// viewer < analyst/data_steward < manager/admin/governance_admin < super_admin.
export const ROLE_RANK = { viewer: 1, analyst: 2, data_steward: 2, manager: 3, admin: 3, tenant_admin: 3, owner: 3, governance_admin: 3, super_admin: 4 };

export function roleRank(role) {
  return ROLE_RANK[role] ?? 1;
}

/** Legacy role-rank check — still used for the main-nav `role:` gate. */
export function canSee(minRole, user) {
  if (!minRole) return true;
  if (user?.role === 'super_admin') return true;
  return roleRank(user?.role) >= roleRank(minRole);
}

/** Capability-based visibility for a nav item/quick-action/widget. */
export function capAllowed(item, user, hasPermission) {
  if (!item) return true;
  if (item.cap) return hasPermission ? !!hasPermission(item.cap) : false;
  if (item.minRole) return canSee(item.minRole, user);
  return true;
}

// ─── Workspace Administration (customer-level) ─────────────────────────────
const ADMIN_CATEGORIES = [
  {
    id: 'people-access',
    label: 'People & Access',
    icon: Shield,
    accent: 'text-cyan-400',
    description: 'Manage users, roles, permissions, data access, authentication and security auditing.',
    audience: 'workspace_admin',
    items: [
      { label: 'Users', description: 'Invite, edit and deactivate user accounts', to: '/access-control?tab=users', icon: Users, cap: 'users:write' },
      { label: 'Roles & Permissions', description: 'Define roles and their permissions', to: '/access-control?tab=roles', icon: ShieldCheck, cap: 'roles:write' },
      { label: 'Data Access Policies', description: 'Row/column policies, masking and data-access rules', to: '/access-control', icon: Shield, cap: 'policies:write' },
      { label: 'Enterprise SSO', description: 'SAML / OIDC / LDAP identity providers', to: '/admin/sso', icon: KeyRound, cap: 'users:write' },
      { label: 'Audit Log', description: 'Security and activity audit trail', to: '/admin/audit-logs', icon: ScrollText, cap: 'audit:read', future: true },
      { label: 'User Activity', description: 'Who is online now, login/logout history and activity statistics', to: '/admin/user-activity', icon: Activity, cap: 'audit:read' },
      { label: 'Export Audit', description: 'Who exported which report/dashboard, in what format and when', to: '/admin/export-logs', icon: FileClock, cap: 'users:read' },
      { label: 'API Keys', description: 'Management API credentials', to: '/admin/api-keys', icon: KeyRound, cap: 'embed:manage', future: true },
    ],
  },
  {
    id: 'ai-configuration',
    label: 'AI Configuration',
    icon: Sparkles,
    accent: 'text-amber-400',
    description: 'Configure AI services, agents, voice, reusable prompts, troubleshooting and usage controls.',
    audience: 'workspace_admin',
    items: [
      { label: 'AI Providers', description: 'LLM providers, keys and cost controls', to: '/admin/llm', icon: Cpu, cap: 'users:read' },
      { label: 'AI Agents', description: 'Automated analysis agents', to: '/admin/ai-agents', icon: Bot, cap: 'users:read' },
      { label: 'Voice', description: 'Wake phrase & Barge-In real-time voice', to: '/admin/voice', icon: Mic, cap: 'users:read' },
      { label: 'AI Coach', description: 'Turn the in-app Coach on/off and pick its avatar', to: '/admin/coach', icon: GraduationCap, cap: 'policies:write' },
      { label: 'Coach Release Validation', description: 'Validate walkthroughs against a product release, repair & roll back', to: '/admin/coach-release', icon: ShieldCheck, cap: 'policies:write' },
      { label: 'Coach Pilot Console', description: 'Controlled-pilot flags, kill switches, metrics, escalations & readiness', to: '/admin/coach-pilot', icon: Rocket, cap: 'policies:write' },
      { label: 'Coach Autonomy', description: 'Capability graph, autonomous opt-in, plan inspector & plan-run review', to: '/admin/coach-autonomy', icon: Bot, cap: 'policies:write' },
      { label: 'Prompt Templates', description: 'Reusable AI prompt library', to: '/admin/prompts', icon: MessageSquare, cap: 'users:read', future: true },
      { label: 'AI Trace Log', description: 'Exact prompts/responses — restricted troubleshooting', to: '/admin/ai-traces', icon: ScrollText, cap: 'platform:engineering' },
      { label: 'AI Usage & Costs', description: 'Token spend and budgets', to: '/admin/ai-cost', icon: DollarSign, cap: 'users:read', future: true },
    ],
  },
  {
    id: 'workspace-settings',
    label: 'Workspace Settings',
    icon: Settings,
    accent: 'text-sky-400',
    description: 'Workspace configuration, branding, email intake, feedback and documentation health.',
    audience: 'workspace_admin',
    items: [
      { label: 'General', description: 'Workspace-level settings', to: '/settings', icon: Settings },
      { label: 'Branding', description: 'Logos, colors and themes', to: '/admin/branding', icon: Sparkles, cap: 'users:read' },
      { label: 'Version Control', description: 'Back up & restore this workspace to your own GitHub repo', to: '/admin/git-backup', icon: GitBranch, cap: 'users:write' },
      { label: 'Email-to-Insight', description: 'Inbound email intake & webhook secrets', to: '/admin/inbound-emails', icon: Mail, cap: 'users:read' },
      { label: 'Feedback Management', description: 'User feedback and feature requests', to: '/admin/feedback', icon: MessageSquare, cap: 'users:read' },
      { label: 'Documentation Analytics', description: 'Help articles ranked by "not helpful" votes', to: '/admin/doc-health', icon: FileClock, cap: 'users:read' },
      { label: 'Pyramid Mapping Console', description: 'Map unknown Pyramid aggregation & data-type codes once, reused by every import', to: '/admin/pyramid-mappings', icon: Pyramid, cap: 'semantic_models:write' },
      { label: 'Schedules', description: 'Every report, export, alert, anomaly-scan and briefing schedule in your tenant — pause, edit or delete', to: '/admin/tenant-schedules', icon: CalendarClock, minRole: 'admin' },
      { label: 'Feature Flags', description: 'Toggle experimental features', to: '/admin/feature-flags', icon: Flag, cap: 'platform:engineering', future: true },
    ],
  },
];

// ─── Cloverleaf Platform Console (super-admin only) ────────────────────────
const PLATFORM_CATEGORIES = [
  {
    id: 'operations',
    label: 'Operations',
    icon: Server,
    accent: 'text-emerald-400',
    description: 'Monitor platform health, deployments, jobs, schedules and system logs.',
    audience: 'platform_console',
    items: [
      { label: 'Operations Overview', description: 'Workspaces, usage rollups & support access', to: '/platform-admin', icon: Gauge, cap: 'platform:operations' },
      { label: 'Platform Health', description: 'Live service health and uptime', to: '/admin/status', icon: Activity, cap: 'platform:operations' },
      { label: 'Deployments', description: 'Production deployment health', to: '/admin/deploy-status', icon: Rocket, cap: 'platform:operations' },
      { label: 'System Updates', description: 'Check for and install new INZARA releases (self-hosted)', to: '/admin/system-updates', icon: Download, cap: 'platform:operations' },
      { label: 'Site Root & Login', description: 'Deployment landing vs sign-in page and login backdrop (white-label)', to: '/admin/site-root', icon: Globe, cap: 'platform:operations' },
      { label: 'Background Jobs', description: 'Queued and running job runners', to: '/admin/jobs', icon: ListChecks, cap: 'platform:operations' },
      { label: 'Global Schedules', description: 'Every scheduled job across tenants', to: '/admin/all-schedules', icon: CalendarClock, cap: 'platform:operations' },
      { label: 'Logs', description: 'Structured platform logs', to: '/system-logs', icon: FileClock, cap: 'platform:operations' },
      { label: 'Support Access', description: 'Time-limited, reason-coded, audited impersonation', to: '/platform-admin', icon: LifeBuoy, cap: 'support_access:use', future: true },
      { label: 'Performance', description: 'Query & rendering performance', to: '/admin/performance', icon: Gauge, cap: 'platform:operations', future: true },
      { label: 'Cache Management', description: 'Inspect and flush caches', to: '/admin/cache', icon: HardDrive, cap: 'platform:operations', future: true },
    ],
  },
  {
    id: 'customer-management',
    label: 'Customer Management',
    icon: Building2,
    accent: 'text-violet-400',
    description: 'Provision workspaces, manage signups, invitations and trial leads.',
    audience: 'platform_console',
    items: [
      { label: 'Tenant Management', description: 'Create and configure tenants', to: '/admin/tenants', icon: Building2, cap: 'platform:customers' },
      { label: 'Signup Requests', description: 'Approve or reject new signups', to: '/admin/signup-requests', icon: UserPlus, cap: 'platform:customers' },
      { label: 'Invitations', description: 'Send and track invitations', to: '/admin/signup-invites', icon: Mailbox, cap: 'platform:customers' },
      { label: 'Trial Leads', description: 'Track and convert trial leads', to: '/admin/trial-leads', icon: Ticket, cap: 'platform:customers' },
      { label: 'Subscriptions', description: 'Workspace subscription state', to: '/admin/subscriptions', icon: BadgeCheck, cap: 'platform:customers', future: true },
      { label: 'Usage & Quotas', description: 'Per-workspace usage and limits', to: '/admin/usage', icon: Gauge, cap: 'platform:customers', future: true },
    ],
  },
  {
    id: 'commercial-operations',
    label: 'Commercial Operations',
    icon: DollarSign,
    accent: 'text-green-400',
    description: 'Cross-customer revenue, subscriptions, licensing and platform usage.',
    audience: 'platform_console',
    items: [
      { label: 'Revenue', description: 'MRR, ARR and revenue analytics', to: '/admin/revenue', icon: DollarSign, cap: 'platform:commercial' },
      { label: 'Subscriptions', description: 'Cross-customer subscription operations', to: '/admin/subscriptions', icon: BadgeCheck, cap: 'platform:commercial', future: true },
      { label: 'Licensing', description: 'Entitlements across customers', to: '/admin/licensing', icon: Receipt, cap: 'platform:commercial', future: true },
      { label: 'Platform Usage', description: 'Aggregate usage across all workspaces', to: '/admin/usage', icon: Gauge, cap: 'platform:commercial', future: true },
    ],
  },
  {
    id: 'billing-usage',
    label: 'Billing & Usage',
    icon: DollarSign,
    accent: 'text-green-400',
    description: 'Plan, invoices, usage metering, limits and licenses for the deployment.',
    audience: 'platform_console',
    items: [
      { label: 'Billing Overview', description: 'Deployment-wide totals and per-tenant billing & AI usage', to: '/admin/platform-billing', icon: DollarSign, cap: 'users:read' },
      { label: 'Plan & Billing', description: 'Manage plan, invoices and payment', to: '/billing', icon: CreditCard, cap: 'users:read' },
      { label: 'Usage', description: 'AI usage metering and cost breakdown', to: '/admin/usage-billing', icon: Gauge, cap: 'users:read' },
      { label: 'Invoices', description: 'Invoice history and receipts', to: '/admin/invoices', icon: Receipt, cap: 'users:read', future: true },
      { label: 'Licenses', description: 'Seats and license entitlements', to: '/admin/licensing', icon: BadgeCheck, cap: 'users:read', future: true },
    ],
  },
  {
    id: 'platform-engineering',
    label: 'Platform Engineering',
    icon: GitBranch,
    accent: 'text-rose-400',
    description: 'Version control, trace logs, feature flags, object storage, cache and system logs.',
    audience: 'platform_console',
    items: [
      { label: 'Version Control', description: 'Cross-workspace semantic-model & content history', to: '/admin/version-control', icon: GitBranch, cap: 'platform:engineering' },
      { label: 'AI Trace Log', description: 'Exact prompts sent to and responses from the AI', to: '/admin/ai-traces', icon: ScrollText, cap: 'platform:engineering' },
      { label: 'Feature Flags', description: 'Toggle experimental features', to: '/admin/feature-flags', icon: Flag, cap: 'platform:engineering' },
      { label: 'Object Storage', description: 'Configure the S3 bucket used for uploads across all tenants', to: '/admin/storage', icon: HardDrive, cap: 'platform:engineering' },
      { label: 'System Logs', description: 'Structured platform logs', to: '/system-logs', icon: FileClock, cap: 'platform:engineering' },
      { label: 'Cache Management', description: 'Inspect and flush caches', to: '/admin/cache', icon: HardDrive, cap: 'platform:engineering', future: true },
    ],
  },
];

const ALL_CATEGORIES = [...ADMIN_CATEGORIES, ...PLATFORM_CATEGORIES];

export { ADMIN_CATEGORIES, PLATFORM_CATEGORIES };

/** Categories for an audience that the user can see at least one item in. */
export function visibleCategories(user, hasPermission, audience = 'workspace_admin') {
  return ALL_CATEGORIES
    .filter((cat) => cat.audience === audience)
    .map((cat) => ({ ...cat, items: cat.items.filter((it) => capAllowed(it, user, hasPermission)) }))
    .filter((cat) => cat.items.length > 0);
}

/** Does the user have access to at least one item in this category? */
export function categoryVisible(cat, user, hasPermission) {
  if (!cat) return false;
  return cat.items.some((it) => capAllowed(it, user, hasPermission));
}

export function findCategory(id) {
  return ALL_CATEGORIES.find((c) => c.id === id) || null;
}
